import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import DuitNowQRGenerator from '../utils/DuitNowQR';
import './TouchNGoPayment.css';

const TouchNGoPayment = ({ 
  amount, 
  orderNumber, 
  onSuccess, 
  onCancel,
  merchantQR // Your static merchant QR from TNG app
}) => {
  const [dynamicQR, setDynamicQR] = useState('');
  const [qrImage, setQrImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(300); // 5 minutes timeout

  useEffect(() => {
    generateDynamicQR();
    
    // Start countdown
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onCancel();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [amount, orderNumber]);

  const generateDynamicQR = async () => {
    try {
      setLoading(true);
      setError('');

      // Your DuitNow merchant QR code (CORRECTED - properly formatted)
      const MERCHANT_QR = '00020101021126520014A000000615000101068900530220MDN173450232761012235204549953034585802MY5908FOODD2PA6008MALAYSIA63045220';
      
      // Get merchant QR from props or hardcoded value
      const baseQR = merchantQR || MERCHANT_QR;

      if (!baseQR) {
        throw new Error('Merchant QR code not configured. Please register as TNG merchant first.');
      }

      console.log('Using merchant QR:', baseQR.substring(0, 20) + '...');

      // Generate dynamic QR with amount
      const qrString = DuitNowQRGenerator.generateDynamicQR(
        baseQR,
        amount,
        '458', // MYR currency code
        orderNumber || `ORD${Date.now()}`
      );

      console.log('Generated dynamic QR:', qrString);
      setDynamicQR(qrString);

      // Generate QR code image
      const qrDataUrl = await QRCode.toDataURL(qrString, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      setQrImage(qrDataUrl);
      setLoading(false);

    } catch (err) {
      console.error('QR generation error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleManualConfirm = () => {
    // Open confirmation dialog
    const confirmed = window.confirm(
      'Have you received the payment in your Touch \'n Go merchant account?\n\n' +
      `Amount: RM ${amount.toFixed(2)}\n` +
      `Order: ${orderNumber}\n\n` +
      'Click OK to confirm payment received.'
    );

    if (confirmed) {
      onSuccess({
        method: 'touchngo',
        amount: amount,
        orderNumber: orderNumber,
        timestamp: new Date().toISOString(),
        status: 'manual_confirmed'
      });
    }
  };

  if (loading) {
    return (
      <div className="tng-payment-container">
        <div className="tng-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Generating QR Code...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tng-payment-container">
        <div className="tng-error">
          <i className="fas fa-exclamation-circle"></i>
          <h3>QR Generation Failed</h3>
          <p>{error}</p>
          <div className="tng-error-help">
            <h4>Setup Required:</h4>
            <ol>
              <li>Register as Touch 'n Go merchant</li>
              <li>Open TNG eWallet app → Merchant Dashboard</li>
              <li>Copy your merchant QR code string</li>
              <li>Add to .env: REACT_APP_TNG_MERCHANT_QR=your_qr_string</li>
            </ol>
            <a 
              href="https://touchngoewallet.onelink.me/8mmV/goecombelanjawanweb"
              target="_blank"
              rel="noopener noreferrer"
              className="tng-register-btn"
            >
              Register as Merchant
            </a>
          </div>
          <button onClick={onCancel} className="tng-cancel-btn">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tng-payment-container">
      <div className="tng-payment-card">
        <div className="tng-header">
          <img 
            src="https://www.touchngo.com.my/assets/brand/tng-ewallet-logo.svg" 
            alt="Touch 'n Go eWallet"
            className="tng-logo"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <h2 style={{ display: 'none' }}>Touch 'n Go Payment</h2>
          <button onClick={onCancel} className="tng-close-btn">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="tng-amount">
          <div className="amount-label">Amount to Pay</div>
          <div className="amount-value">RM {amount.toFixed(2)}</div>
          <div className="order-ref">Order: {orderNumber}</div>
        </div>

        <div className="tng-qr-section">
          <div className="qr-instruction">
            <i className="fas fa-mobile-alt"></i>
            <p>Scan with Touch 'n Go eWallet</p>
            <small>Amount will be automatically filled</small>
          </div>

          <div className="qr-code-wrapper">
            {qrImage && (
              <img 
                src={qrImage} 
                alt="Payment QR Code" 
                className="qr-code-image"
              />
            )}
          </div>

          <div className="qr-timer">
            <i className="fas fa-clock"></i>
            <span>Expires in {formatTime(countdown)}</span>
          </div>
        </div>

        <div className="tng-instructions">
          <h4><i className="fas fa-info-circle"></i> How to Pay:</h4>
          <ol>
            <li>Open Touch 'n Go eWallet app</li>
            <li>Tap <strong>Scan</strong> button</li>
            <li>Scan this QR code</li>
            <li>Verify amount (RM {amount.toFixed(2)})</li>
            <li>Confirm payment in your app</li>
          </ol>
        </div>

        <div className="tng-actions">
          <button 
            onClick={handleManualConfirm}
            className="tng-confirm-btn"
          >
            <i className="fas fa-check-circle"></i>
            I've Received Payment
          </button>
          
          <button 
            onClick={onCancel}
            className="tng-cancel-secondary-btn"
          >
            Cancel Payment
          </button>
        </div>

        <div className="tng-footer-note">
          <i className="fas fa-shield-alt"></i>
          <small>Payment is processed securely by Touch 'n Go eWallet</small>
        </div>
      </div>
    </div>
  );
};

export default TouchNGoPayment;
